import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import {
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  ChevronRightIcon,
  DocumentPlusIcon,
  EllipsisHorizontalIcon,
  EyeIcon,
  MusicalNoteIcon,
  PencilIcon,
  PencilSquareIcon,
  PlayIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import { DocumentIcon, FolderIcon } from '@heroicons/react/24/solid'
import { FilesService, type BrandFileRead, type Creative, type FileFolder } from '../client'
import { apiErrorMessage } from '../lib/api'
import { useAuth } from '../providers/auth/useAuth'
import FileViewer, { type FileItem } from '../components/files/FileViewer'
import Modal from '../components/ui/Modal'

type Folder = 'creatives' | FileFolder

const FOLDERS: { key: Folder; name: string; empty: string }[] = [
  { key: 'creatives', name: 'Creatives', empty: 'Ads you generate show up here.' },
  { key: 'uploads', name: 'Uploads', empty: 'Upload files or write a note. Files you attach in a chat land here too.' },
  { key: 'artifacts', name: 'Artifacts', empty: 'Documents the chat writes for you will show up here.' },
]

const BADGE_COLOURS: Record<string, string> = {
  md: 'bg-blue-500',
  doc: 'bg-blue-500',
  docx: 'bg-blue-500',
  txt: 'bg-stone-500',
  pdf: 'bg-red-500',
  csv: 'bg-emerald-500',
  xls: 'bg-emerald-500',
  xlsx: 'bg-emerald-500',
  json: 'bg-amber-500',
}

const MENU_WIDTH = 176
const MENU_HEIGHT = 220
const MAX_FILES = 500
const BUTTON =
  'inline-flex h-9 items-center gap-1.5 rounded-lg border border-stone-300 bg-white px-3 text-sm font-medium text-stone-700 transition-colors hover:bg-stone-50 disabled:opacity-50'
const PRIMARY =
  'inline-flex h-9 items-center gap-1.5 rounded-lg bg-linear-to-b from-stone-700 to-stone-950 px-3 text-sm font-medium text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.12),0_1px_2px_rgba(28,25,23,0.2)] transition-opacity hover:opacity-90 disabled:opacity-60'
const INPUT =
  'w-full rounded-lg border border-stone-300 px-3 py-2 text-sm text-stone-900 placeholder:text-stone-400 focus:border-purple-400 focus:ring-2 focus:ring-purple-100 focus:outline-none'
const MENU_ITEM = 'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[13px]'
const MENU_OPTION = `${MENU_ITEM} hover:bg-stone-100`
const TREE_ROW = 'flex h-8 w-full min-w-0 items-center gap-2 rounded-md px-2 text-left text-sm transition-colors'
const GRID = 'grid grid-cols-[repeat(auto-fill,minmax(110px,1fr))] gap-3'

function fileItem(file: BrandFileRead): FileItem {
  return { key: file.id, name: file.name, url: file.url, kind: file.kind, file }
}

function DriveLogo({ className }: { className: string }) {
  return (
    <svg viewBox="0 0 87.3 78" aria-hidden="true" className={className}>
      <path d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z" fill="#0066da" />
      <path d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0 -1.2 4.5h27.5z" fill="#00ac47" />
      <path d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l5.852 11.5z" fill="#ea4335" />
      <path d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z" fill="#00832d" />
      <path d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z" fill="#2684fc" />
      <path d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z" fill="#ffba00" />
    </svg>
  )
}

function Thumb({ item }: { item: FileItem }) {
  if (item.kind === 'image') {
    return (
      <img
        src={item.url}
        alt=""
        loading="lazy"
        className="max-h-[88px] max-w-[88px] rounded-md border border-stone-200 bg-white object-cover shadow-sm"
      />
    )
  }
  if (item.kind === 'video') {
    return (
      <span className="flex h-[88px] w-[100px] items-center justify-center rounded-md bg-stone-900">
        <PlayIcon className="size-9 text-white" />
      </span>
    )
  }
  if (item.kind === 'audio') {
    return (
      <span className="flex size-[88px] items-center justify-center rounded-md bg-amber-50">
        <MusicalNoteIcon className="size-9 text-amber-500" />
      </span>
    )
  }
  const extension = item.name.includes('.') ? (item.name.split('.').pop() ?? '').toLowerCase() : ''
  return (
    <span className="relative flex h-[88px] w-[66px] flex-col gap-[5px] rounded-md border border-stone-200 bg-white px-2.5 py-2.5 shadow-sm">
      {['w-4/5', 'w-3/5', 'w-4/5', 'w-2/5', 'w-3/5'].map((width, index) => (
        <span key={index} className={`h-[3px] rounded-full bg-stone-400/25 ${width}`} />
      ))}
      <span className="absolute right-1.5 bottom-1.5 flex items-center gap-0.5 text-[8px] font-bold tracking-wide text-stone-500">
        <span className={`size-1.5 rounded-full ${BADGE_COLOURS[extension] ?? 'bg-stone-400'}`} />
        {extension.slice(0, 4).toUpperCase() || 'FILE'}
      </span>
    </span>
  )
}

export default function Files() {
  const { brand } = useAuth()
  const brandId = brand?.id
  const [files, setFiles] = useState<BrandFileRead[] | null>(null)
  const [creatives, setCreatives] = useState<Creative[] | null>(null)
  const [limit, setLimit] = useState(MAX_FILES)
  const [error, setError] = useState<string | null>(null)
  const [folder, setFolder] = useState<Folder | null>(null)
  const [selected, setSelected] = useState<string | null>(null)
  const [viewing, setViewing] = useState<{ item: FileItem; editing: boolean } | null>(null)
  const [menu, setMenu] = useState<{ item: FileItem; x: number; y: number } | null>(null)
  const [renaming, setRenaming] = useState<{ file: BrandFileRead; name: string } | null>(null)
  const [deleting, setDeleting] = useState<BrandFileRead | null>(null)
  const [noteName, setNoteName] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const uploadInput = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!brandId) return
    FilesService.listFiles(brandId)
      .then((list) => {
        setFiles(list.files)
        setLimit(list.limit ?? MAX_FILES)
      })
      .catch((err: unknown) => {
        setFiles([])
        setError(apiErrorMessage(err))
      })
    FilesService.listCreatives(brandId)
      .then((list) => setCreatives(list.creatives))
      .catch((err: unknown) => {
        setCreatives([])
        setError(apiErrorMessage(err))
      })
  }, [brandId])

  useEffect(() => {
    if (!menu) return
    const close = (event: KeyboardEvent) => event.key === 'Escape' && setMenu(null)
    document.addEventListener('keydown', close)
    return () => document.removeEventListener('keydown', close)
  }, [menu])

  function replaceFile(file: BrandFileRead) {
    setFiles((current) => current && current.map((existing) => (existing.id === file.id ? file : existing)))
  }

  function itemsIn(key: Folder): FileItem[] {
    if (key === 'creatives') {
      return (creatives ?? []).map((creative) => ({
        key: `creative-${creative.id}`,
        name: creative.name,
        url: creative.url,
        kind: 'image',
        file: null,
      }))
    }
    return (files ?? [])
      .filter((file) => file.folder === key)
      .map(fileItem)
  }

  function openMenu(item: FileItem, x: number, y: number) {
    setSelected(item.key)
    setMenu({
      item,
      x: Math.min(x, window.innerWidth - MENU_WIDTH - 8),
      y: Math.min(y, window.innerHeight - MENU_HEIGHT),
    })
  }

  async function upload(chosen: File[]) {
    if (!brandId) return
    setBusy(true)
    setFolder('uploads')
    for (const file of chosen) {
      try {
        const saved = await FilesService.uploadFile(brandId, { file })
        setFiles((current) => [saved, ...(current ?? [])])
      } catch (err) {
        toast.error(`${file.name}: ${apiErrorMessage(err)}`)
      }
    }
    setBusy(false)
  }

  async function createNote() {
    const name = noteName?.trim()
    if (!brandId || busy || !name) return
    setBusy(true)
    try {
      const note = await FilesService.createNote(brandId, { name })
      setFiles((current) => [note, ...(current ?? [])])
      setNoteName(null)
      setFolder('uploads')
      setViewing({ item: fileItem(note), editing: true })
    } catch (err) {
      toast.error(apiErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  async function rename() {
    if (!brandId || !renaming) return
    const name = renaming.name.trim()
    if (!name || name === renaming.file.name) {
      setRenaming(null)
      return
    }
    setBusy(true)
    try {
      replaceFile(await FilesService.renameFile(renaming.file.id, brandId, { name }))
      setRenaming(null)
    } catch (err) {
      toast.error(apiErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  async function remove() {
    if (!brandId || !deleting) return
    setBusy(true)
    try {
      await FilesService.deleteFile(deleting.id, brandId)
      setFiles((current) => current && current.filter(({ id }) => id !== deleting.id))
      setDeleting(null)
    } catch (err) {
      toast.error(apiErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  const loading = files === null || creatives === null
  const items = folder ? itemsIn(folder) : []

  return (
    <div className="flex grow basis-0 flex-col overflow-hidden">
      <header className="flex flex-wrap items-end justify-between gap-4 border-b border-stone-200 px-4 pt-8 pb-4 md:px-6">
        <div className="min-w-0">
          <h1 className="font-heading text-2xl font-semibold text-stone-900 md:text-3xl">Files</h1>
          <p className="mt-1 text-sm text-stone-500">
            Your brand's documents, notes and media. The chat opens the ones a task needs.
          </p>
        </div>
        <div className="flex gap-2">
          <button type="button" disabled={busy} onClick={() => setNoteName('')} className={BUTTON}>
            <DocumentPlusIcon className="size-4" />
            New note
          </button>
          <button type="button" disabled={busy} onClick={() => uploadInput.current?.click()} className={PRIMARY}>
            <ArrowUpTrayIcon className="size-4" />
            Upload
          </button>
          <input
            ref={uploadInput}
            type="file"
            multiple
            aria-label="Upload files"
            className="hidden"
            onChange={(event) => {
              const chosen = Array.from(event.target.files ?? [])
              event.target.value = ''
              if (chosen.length) void upload(chosen)
            }}
          />
        </div>
      </header>

      <div className="flex gap-1.5 overflow-x-auto border-b border-stone-200 px-4 py-2 [scrollbar-width:none] md:hidden">
        {FOLDERS.map(({ key, name }) => (
          <button
            key={name}
            type="button"
            onClick={() => setFolder(folder === key ? null : key)}
            className={`shrink-0 rounded-full px-3 py-1.5 text-sm ${
              folder === key ? 'bg-stone-900 text-white' : 'bg-white text-stone-600 ring-1 ring-stone-200'
            }`}
          >
            {name}
          </button>
        ))}
      </div>

      <div className="flex min-h-0 flex-1">
        <aside className="hidden w-60 shrink-0 flex-col overflow-y-auto border-r border-stone-200 p-2 [scrollbar-width:thin] md:flex">
          {FOLDERS.map(({ key, name }) => {
            const open = folder === key
            return (
              <div key={key}>
                <button
                  type="button"
                  onClick={() => setFolder(open ? null : key)}
                  className={`${TREE_ROW} ${open ? 'bg-stone-200/70 font-medium text-stone-900' : 'text-stone-600 hover:bg-stone-900/[0.04]'}`}
                >
                  <ChevronRightIcon className={`size-3.5 shrink-0 text-stone-400 transition-transform ${open ? 'rotate-90' : ''}`} />
                  <FolderIcon className="size-4 shrink-0 text-amber-400" />
                  <span className="truncate">{name}</span>
                </button>
                {open &&
                  itemsIn(key).map((item) => (
                    <button
                      key={item.key}
                      type="button"
                      title={item.name}
                      onClick={() => setViewing({ item, editing: false })}
                      className={`${TREE_ROW} pl-[30px] text-stone-500 hover:bg-stone-900/[0.04] hover:text-stone-800`}
                    >
                      <DocumentIcon className="size-3.5 shrink-0 text-stone-300" />
                      <span className="truncate">{item.name}</span>
                    </button>
                  ))}
              </div>
            )
          })}
          <div title="Coming soon" className={`${TREE_ROW} cursor-not-allowed pl-[30px] text-stone-400`}>
            <FolderIcon className="size-4 shrink-0 text-stone-300" />
            My Drive
            <span className="ml-auto flex items-center gap-1.5">
              <DriveLogo className="size-3.5" />
              <span className="rounded-full bg-stone-200 px-1.5 py-0.5 text-[10px] font-medium text-stone-500">Soon</span>
            </span>
          </div>
          {files && (
            <p className="mt-auto px-2 pt-4 text-xs text-stone-400">
              {files.length} of {limit} files
            </p>
          )}
        </aside>

        <main
          className="min-w-0 flex-1 overflow-y-auto p-4 [scrollbar-width:thin]"
          onClick={(event) => {
            if (event.target === event.currentTarget) setSelected(null)
          }}
        >
          {error && <p className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600">{error}</p>}
          {loading ? (
            <div className={`${GRID} animate-pulse`}>
              {Array.from({ length: 10 }, (_, index) => (
                <div key={index} className="flex flex-col items-center gap-2 p-2">
                  <div className="h-[88px] w-[72px] rounded-md bg-stone-200" />
                  <div className="h-2.5 w-16 rounded bg-stone-200" />
                </div>
              ))}
            </div>
          ) : folder === null ? (
            <div className={GRID}>
              {FOLDERS.map(({ key, name }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setFolder(key)}
                  className="flex flex-col items-center gap-1.5 rounded-lg p-2 text-center hover:bg-stone-900/[0.04]"
                >
                  <FolderIcon className="size-16 text-amber-300" />
                  <span className="text-xs leading-tight text-stone-700">{name}</span>
                  <span className="text-[11px] text-stone-400">{itemsIn(key).length} items</span>
                </button>
              ))}
              <div title="Coming soon" className="flex cursor-not-allowed flex-col items-center gap-1.5 p-2 text-center">
                <FolderIcon className="size-16 text-stone-200" />
                <span className="text-xs leading-tight text-stone-400">My Drive</span>
                <span className="flex items-center gap-1 text-[11px] text-stone-400">
                  <DriveLogo className="size-3" />
                  Coming soon
                </span>
              </div>
            </div>
          ) : items.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center">
              <span className="flex size-10 items-center justify-center rounded-lg bg-stone-200">
                <FolderIcon className="size-5 text-stone-400" />
              </span>
              <p className="font-heading text-lg font-medium text-stone-900">This folder is empty</p>
              <p className="max-w-sm text-sm text-stone-500">{FOLDERS.find(({ key }) => key === folder)?.empty}</p>
            </div>
          ) : (
            <div className={GRID}>
              {items.map((item) => (
                <div key={item.key} className="group relative">
                  <button
                    type="button"
                    aria-pressed={selected === item.key}
                    onClick={() =>
                      window.matchMedia('(pointer: coarse)').matches
                        ? setViewing({ item, editing: false })
                        : setSelected(item.key)
                    }
                    onDoubleClick={() => setViewing({ item, editing: false })}
                    onKeyDown={(event) => event.key === 'Enter' && setViewing({ item, editing: false })}
                    onContextMenu={(event) => {
                      event.preventDefault()
                      openMenu(item, event.clientX, event.clientY)
                    }}
                    className="flex w-full flex-col items-center gap-1.5 rounded-lg p-2 text-center transition-colors hover:bg-stone-900/[0.04] aria-pressed:bg-stone-900/[0.08]"
                  >
                    <span className="flex h-[88px] items-center justify-center">
                      <Thumb item={item} />
                    </span>
                    <span className="line-clamp-2 text-xs leading-tight break-words text-stone-700">{item.name}</span>
                  </button>
                  <button
                    type="button"
                    aria-label={`More for ${item.name}`}
                    onClick={(event) => {
                      const { left, bottom } = event.currentTarget.getBoundingClientRect()
                      openMenu(item, left, bottom)
                    }}
                    className="absolute top-1 right-1 flex size-10 items-center justify-center rounded-md bg-white/90 text-stone-500 opacity-0 shadow-sm transition group-hover:opacity-100 hover:text-stone-900 focus-visible:opacity-100 md:size-8 pointer-coarse:opacity-100"
                  >
                    <EllipsisHorizontalIcon className="size-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </main>
      </div>

      {menu && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setMenu(null)}
            onContextMenu={(event) => {
              event.preventDefault()
              setMenu(null)
            }}
          />
          <div
            role="menu"
            style={{ left: menu.x, top: menu.y, width: MENU_WIDTH }}
            className="fixed z-50 rounded-lg border border-stone-200 bg-white p-1 text-stone-700 shadow-lg"
            onClick={() => setMenu(null)}
          >
            <button type="button" role="menuitem" onClick={() => setViewing({ item: menu.item, editing: false })} className={MENU_OPTION}>
              <EyeIcon className="size-4 text-stone-500" />
              Open
            </button>
            {menu.item.file?.editable && (
              <button type="button" role="menuitem" onClick={() => setViewing({ item: menu.item, editing: true })} className={MENU_OPTION}>
                <PencilSquareIcon className="size-4 text-stone-500" />
                Edit
              </button>
            )}
            {menu.item.file && (
              <button
                type="button"
                role="menuitem"
                onClick={() => menu.item.file && setRenaming({ file: menu.item.file, name: menu.item.file.name })}
                className={MENU_OPTION}
              >
                <PencilIcon className="size-4 text-stone-500" />
                Rename
              </button>
            )}
            <a
              role="menuitem"
              href={menu.item.url}
              download={menu.item.name}
              target="_blank"
              rel="noopener noreferrer"
              className={MENU_OPTION}
            >
              <ArrowDownTrayIcon className="size-4 text-stone-500" />
              Download
            </a>
            {menu.item.file && (
              <>
                <div className="my-1 border-t border-stone-100" />
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => setDeleting(menu.item.file)}
                  className={`${MENU_ITEM} text-red-600 hover:bg-red-50`}
                >
                  <TrashIcon className="size-4" />
                  Delete
                </button>
              </>
            )}
          </div>
        </>
      )}

      {viewing && brandId && (
        <FileViewer
          key={viewing.item.key}
          brandId={brandId}
          item={viewing.item}
          startEditing={viewing.editing}
          onSaved={(file) => {
            replaceFile(file)
            setViewing({ ...viewing, item: { ...viewing.item, file } })
          }}
          onClose={() => setViewing(null)}
        />
      )}

      {noteName !== null && (
        <Modal title="New note" width="max-w-md" onClose={() => setNoteName(null)}>
          <p className="mb-3 text-sm text-stone-500">Creates an empty markdown note in Uploads and opens it.</p>
          <input
            autoFocus
            value={noteName}
            placeholder="filename.md"
            onChange={(event) => setNoteName(event.target.value)}
            onKeyDown={(event) => event.key === 'Enter' && void createNote()}
            className={INPUT}
          />
          <div className="mt-4 flex justify-end gap-2">
            <button type="button" onClick={() => setNoteName(null)} className={BUTTON}>
              Cancel
            </button>
            <button type="button" disabled={busy || !noteName.trim()} onClick={() => void createNote()} className={PRIMARY}>
              Create
            </button>
          </div>
        </Modal>
      )}

      {renaming && (
        <Modal title="Rename file" width="max-w-md" onClose={() => setRenaming(null)}>
          <input
            autoFocus
            value={renaming.name}
            onChange={(event) => setRenaming({ ...renaming, name: event.target.value })}
            onKeyDown={(event) => event.key === 'Enter' && void rename()}
            className={INPUT}
          />
          <div className="mt-4 flex justify-end gap-2">
            <button type="button" onClick={() => setRenaming(null)} className={BUTTON}>
              Cancel
            </button>
            <button type="button" disabled={busy || !renaming.name.trim()} onClick={() => void rename()} className={PRIMARY}>
              Rename
            </button>
          </div>
        </Modal>
      )}

      {deleting && (
        <Modal title={`Delete ${deleting.name}?`} width="max-w-md" onClose={() => setDeleting(null)}>
          <p className="text-sm text-stone-600">
            It is removed from Files and the chat can no longer read it. Chats that already used it keep their copy.
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <button type="button" onClick={() => setDeleting(null)} className={BUTTON}>
              Cancel
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => void remove()}
              className="inline-flex h-9 items-center rounded-lg bg-red-600 px-3 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-60"
            >
              Delete
            </button>
          </div>
        </Modal>
      )}
    </div>
  )
}
